/**
 * Phase 21β-2.6 — Vertragsvorlagen (Contract Templates).
 *
 * 100vh kompakte Liste mit Split-View-Vorschau – analog zu /vorlagen, aber
 * zugeschnitten auf modulare Mietverträge mit Bausteinen.
 *
 *   ┌── Topbar (Suche · Neue Vertragsvorlage) ────────────────────┐
 *   ├── 220px ──┬─────────── 1fr ──────────────────────────────────┤
 *   │ Filter    │  Liste (Name · Typ · Bausteine · Status · …)     │
 *   │  Typ      ├──── Split-Preview unten (Block-Liste + Notiz) ──┤
 *   └───────────┴────────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Briefcase, MagnifyingGlass, Plus, FileText, ShieldCheck, Archive,
  ArrowCounterClockwise, DotsThreeVertical, PencilSimple, Copy, X,
  FunnelSimple, CheckCircle, Circle, Stack, FileArrowUp, Spinner,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CONTRACT_TYPES = [
  { key: "wohnraum", label: "Wohnraum", tone: "emerald" },
  { key: "gewerbe", label: "Gewerbe", tone: "indigo" },
  { key: "stellplatz", label: "Stellplatz", tone: "amber" },
  { key: "garage", label: "Garage", tone: "violet" },
  { key: "sonstige", label: "Sonstige", tone: "slate" },
];
const TONE_CSS = {
  emerald: "bg-emerald-50 text-emerald-700",
  indigo: "bg-indigo-50 text-indigo-700",
  amber: "bg-amber-50 text-amber-800",
  violet: "bg-violet-50 text-violet-700",
  slate: "bg-slate-100 text-slate-700",
};

const typeMeta = (key) => CONTRACT_TYPES.find((t) => t.key === key) || CONTRACT_TYPES[4];
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "2-digit",
    });
  } catch { return "—"; }
};

export default function ContractTemplates() {
  const navigate = useNavigate();
  const confirmDialog = useConfirm();

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [activeType, setActiveType] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedFull, setSelectedFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ocrBusy, setOcrBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/contract-templates",
        { params: { include_archived: true } });
      setItems(data || []);
      return data || [];
    } catch (e) { toast.error(formatApiError(e)); return []; }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  // Categories with counts (by contract_type)
  const typeCounts = useMemo(() => {
    const counts = {};
    items.forEach((t) => {
      if (t.is_archived && !showArchived) return;
      const k = t.contract_type || "sonstige";
      counts[k] = (counts[k] || 0) + 1;
    });
    return counts;
  }, [items, showArchived]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      if (t.is_archived && !showArchived) return false;
      if (activeType && t.contract_type !== activeType) return false;
      if (!q) return true;
      const hay = [t.name, t.description, t.contract_type].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, activeType, showArchived]);

  useEffect(() => {
    if (filtered.length === 0) { setSelected(null); return; }
    if (!selected || !filtered.find((t) => t.id === selected.id)) {
      setSelected(filtered[0]);
    }
  }, [filtered]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load full detail (with blocks) for preview pane
  useEffect(() => {
    let cancel = false;
    if (!selected) { setSelectedFull(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/contract-templates/${selected.id}`);
        if (!cancel) setSelectedFull(data);
      } catch (e) {
        if (!cancel) {
          setSelectedFull(null);
          toast.error(formatApiError(e));
        }
      }
    })();
    return () => { cancel = true; };
  }, [selected]);

  const totalActive = items.filter((t) => !t.is_archived || showArchived).length;
  const openEditor = (tpl) => navigate(`/vertragsvorlagen/${tpl.id}/bearbeiten`);

  const createNew = async () => {
    try {
      const { data } = await api.post("/contract-templates", {
        name: "Neue Vertragsvorlage",
        contract_type: activeType || "wohnraum",
        description: "",
        status: "draft",
      });
      navigate(`/vertragsvorlagen/${data.id}/bearbeiten`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const duplicate = async (tpl) => {
    try {
      await api.post(`/contract-templates/${tpl.id}/duplicate`);
      toast.success("Dupliziert"); await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (tpl, archived) => {
    if (archived) {
      const ok = await confirmDialog({
        title: `„${tpl.name}" archivieren?`,
        description: "Kann jederzeit wiederhergestellt werden.",
        confirmText: "Archivieren", tone: "warn",
      });
      if (!ok) return;
    }
    try {
      await api.post(`/contract-templates/${tpl.id}/archive`, { archived });
      toast.success(archived ? "Archiviert" : "Wiederhergestellt"); await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (tpl) => {
    if (tpl.is_default) {
      toast.error("Standard-Vorlagen nur archivierbar."); return;
    }
    const ok = await confirmDialog({
      title: "Vertragsvorlage endgültig löschen?",
      description: `„${tpl.name}" wird unwiderruflich entfernt.`,
      confirmText: "Endgültig löschen", tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/contract-templates/${tpl.id}`);
      toast.success("Gelöscht"); await reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // OCR import — PDF or image → draft contract template
  const ocrImport = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Datei zu groß (max 25 MB)"); return;
    }
    setOcrBusy(true);
    const tid = toast.loading("OCR läuft – das kann 10–30 Sekunden dauern …");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("contract_type", "wohnraum");
      const { data } = await api.post("/contract-templates/import/ocr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.dismiss(tid);
      toast.success(`Importiert: ${(data.blocks || []).length} Bausteine erkannt`);
      await reload();
      navigate(`/vertragsvorlagen/${data.id}/bearbeiten`);
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiError(e));
    } finally {
      setOcrBusy(false);
    }
  };

  return (
    <div
      className="h-[calc(100vh-64px)] flex flex-col bg-slate-50"
      data-testid="contract-templates-library"
    >
      {/* Topbar */}
      <header className="px-4 lg:px-6 py-2.5 bg-white border-b border-slate-200 flex items-center gap-2 flex-wrap shrink-0">
        <Briefcase weight="duotone" size={20} className="text-emerald-600 shrink-0" />
        <h1 className="text-sm font-semibold tracking-tight text-slate-900">Vertragsvorlagen</h1>
        <span className="text-[11px] text-slate-500 ml-1">{filtered.length} / {totalActive}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative w-56">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Vertragsvorlagen durchsuchen…"
              className="pl-8 h-8 bg-slate-50 text-xs"
              data-testid="ctpl-search"
            />
          </div>
          <Button
            onClick={createNew} size="sm"
            className="bg-slate-900 hover:bg-slate-800 gap-1.5 h-8"
            data-testid="ctpl-new-btn"
          >
            <Plus size={14} /> Neue Vertragsvorlage
          </Button>
          <input
            type="file" id="ctpl-ocr-input" className="hidden"
            accept="application/pdf,image/png,image/jpeg,image/tiff"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) ocrImport(f);
            }}
          />
          <Button
            size="sm" variant="outline" disabled={ocrBusy}
            onClick={() => document.getElementById("ctpl-ocr-input")?.click()}
            className="h-8 gap-1.5"
            data-testid="ctpl-ocr-btn"
            title="PDF oder Bild eines Vertrages hochladen – Tesseract erkennt den Text und legt eine Entwurfs-Vertragsvorlage an."
          >
            {ocrBusy ? <Spinner size={14} className="animate-spin" /> : <FileArrowUp size={14} />}
            OCR-Import
          </Button>
        </div>
      </header>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[220px_1fr]">
        {/* Filter sidebar */}
        <aside
          className="hidden lg:flex flex-col border-r border-slate-200 bg-white overflow-y-auto"
          data-testid="ctpl-filters"
        >
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-1">
              <FunnelSimple size={10} /> Vertragstyp
            </div>
            <ul className="space-y-0.5">
              <FilterRow
                active={!activeType} count={totalActive}
                onClick={() => setActiveType(null)} testid="ctype-all"
              >
                Alle Verträge
              </FilterRow>
              {CONTRACT_TYPES.map((ct) => (
                <FilterRow
                  key={ct.key}
                  active={activeType === ct.key}
                  count={typeCounts[ct.key] || 0}
                  onClick={() => setActiveType(ct.key)}
                  testid={`ctype-${ct.key}`}
                >
                  {ct.label}
                </FilterRow>
              ))}
            </ul>
          </div>
          <div className="px-3 py-2">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer text-slate-700">
              <input
                type="checkbox" checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="accent-slate-900 w-3 h-3"
                data-testid="ctpl-show-archived"
              />
              Archivierte zeigen
            </label>
          </div>
          {/* Module-Switch */}
          <div className="mt-auto px-3 py-2 border-t border-slate-100 space-y-1">
            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Module</div>
            <button
              onClick={() => navigate("/vorlagen")}
              className="block w-full text-left text-[11px] text-slate-600 px-1.5 py-1 rounded-sm hover:bg-slate-50"
              data-testid="nav-back-vorlagen"
            >
              Vorlagen
            </button>
            <div className="text-[11px] text-emerald-700 px-1.5 py-1 bg-emerald-50 rounded-sm border border-emerald-100 inline-flex items-center gap-1">
              <Briefcase size={10} /> Vertragsvorlagen (aktiv)
            </div>
          </div>
        </aside>

        {/* Main: Table + Split-Preview */}
        <main className="flex flex-col min-h-0" data-testid="ctpl-main">
          {/* Table */}
          <div className="flex-[3] overflow-y-auto bg-white border-b border-slate-200" data-testid="ctpl-table-wrap">
            <table className="w-full text-xs" data-testid="ctpl-table">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="text-left px-3 py-2 w-[35%]">Name</th>
                  <th className="text-left px-2 py-2 w-[15%]">Typ</th>
                  <th className="text-left px-2 py-2 w-[14%]">Bausteine</th>
                  <th className="text-left px-2 py-2 w-[14%]">Status</th>
                  <th className="text-left px-2 py-2 w-[14%]">Geändert</th>
                  <th className="w-[8%] py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="text-center text-slate-500 py-8">Lade…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-500 py-12">
                    <Briefcase size={28} className="mx-auto text-slate-300 mb-2" />
                    Keine Vertragsvorlagen gefunden
                  </td></tr>
                )}
                {filtered.map((tpl) => {
                  const isSel = selected?.id === tpl.id;
                  const tm = typeMeta(tpl.contract_type);
                  return (
                    <tr
                      key={tpl.id}
                      onClick={() => setSelected(tpl)}
                      onDoubleClick={() => openEditor(tpl)}
                      data-testid={`ctpl-row-${tpl.id}`}
                      className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                        isSel ? "bg-emerald-50/60 hover:bg-emerald-50" : ""
                      } ${tpl.is_archived ? "opacity-60" : ""}`}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={13} className="text-slate-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-900 truncate">{tpl.name}</div>
                            {tpl.description && (
                              <div className="text-[10px] text-slate-500 truncate">{tpl.description}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${TONE_CSS[tm.tone]}`}>
                          {tm.label}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-[10px] text-slate-600 tabular-nums">
                        <span className="inline-flex items-center gap-1">
                          <Stack size={10} className="text-slate-400" />
                          {tpl.enabled_count || 0}/{tpl.block_count || 0}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        {tpl.is_archived ? (
                          <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                            <Archive size={9} /> Archiv
                          </span>
                        ) : tpl.is_default ? (
                          <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5">
                            <ShieldCheck size={9} /> Standard
                          </span>
                        ) : tpl.status === "draft" ? (
                          <span className="text-[10px] text-amber-700">Entwurf</span>
                        ) : (
                          <span className="text-[10px] text-slate-500">aktiv</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-[10px] text-slate-500 tabular-nums">
                        {fmtDate(tpl.updated_at || tpl.created_at)}
                      </td>
                      <td
                        onClick={(e) => e.stopPropagation()}
                        className="text-right pr-2"
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="text-slate-400 hover:text-slate-700 p-1 rounded-sm"
                              data-testid={`ctpl-menu-${tpl.id}`}
                            >
                              <DotsThreeVertical size={14} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem onClick={() => openEditor(tpl)} data-testid={`ctpl-edit-${tpl.id}`}>
                              <PencilSimple size={13} className="mr-2" /> Bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => duplicate(tpl)} data-testid={`ctpl-duplicate-${tpl.id}`}>
                              <Copy size={13} className="mr-2" /> Duplizieren
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {tpl.is_archived ? (
                              <DropdownMenuItem onClick={() => archive(tpl, false)} data-testid={`ctpl-restore-${tpl.id}`}>
                                <ArrowCounterClockwise size={13} className="mr-2" /> Wiederherstellen
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => archive(tpl, true)} data-testid={`ctpl-archive-${tpl.id}`}>
                                <Archive size={13} className="mr-2" /> Archivieren
                              </DropdownMenuItem>
                            )}
                            {!tpl.is_default && (
                              <DropdownMenuItem
                                onClick={() => remove(tpl)} className="text-rose-700"
                                data-testid={`ctpl-delete-${tpl.id}`}
                              >
                                <X size={13} className="mr-2" /> Löschen
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Split-View Preview */}
          <div
            className="flex-[2] overflow-hidden bg-white grid grid-cols-1 lg:grid-cols-[1fr_280px]"
            data-testid="ctpl-preview-pane"
          >
            {selected ? (
              <>
                <div className="overflow-y-auto p-4 border-r border-slate-100">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
                        Bausteine
                      </div>
                      <div className="text-sm font-semibold text-slate-900 truncate">{selected.name}</div>
                    </div>
                    <Button
                      size="sm" onClick={() => openEditor(selected)}
                      className="bg-slate-900 hover:bg-slate-800 h-7 gap-1 text-xs"
                      data-testid="ctpl-open-editor"
                    >
                      <PencilSimple size={12} /> Öffnen
                    </Button>
                  </div>
                  {selectedFull ? (
                    <ul className="space-y-1" data-testid="ctpl-block-list">
                      {(selectedFull.blocks || []).map((b) => (
                        <li
                          key={b.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border ${
                            b.enabled === false
                              ? "border-dashed border-slate-200 bg-slate-50/50 text-slate-500"
                              : "border-slate-100 bg-white text-slate-800"
                          }`}
                        >
                          {b.enabled === false ? (
                            <Circle size={12} className="text-slate-300 shrink-0" />
                          ) : (
                            <CheckCircle weight="fill" size={12} className="text-emerald-500 shrink-0" />
                          )}
                          <span className="text-xs font-medium flex-1 truncate">{b.title || "Ohne Titel"}</span>
                          {b.optional && (
                            <span className="text-[9px] uppercase text-amber-700 bg-amber-50 px-1 py-0.5 rounded-sm">
                              optional
                            </span>
                          )}
                          {b.category && (
                            <span className="text-[9px] text-slate-500">{b.category}</span>
                          )}
                        </li>
                      ))}
                      {(selectedFull.blocks || []).length === 0 && (
                        <li className="text-xs text-slate-400 italic">Noch keine Bausteine.</li>
                      )}
                    </ul>
                  ) : (
                    <div className="text-xs text-slate-400">Lade Bausteine…</div>
                  )}
                </div>
                <div className="hidden lg:block overflow-y-auto p-3 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    Eigenschaften
                  </div>
                  <DetailRow label="Typ" value={typeMeta(selected.contract_type).label} />
                  <DetailRow label="Standard" value={selected.is_default ? "Ja" : "Nein"} />
                  <DetailRow label="Status" value={selected.status || "draft"} />
                  <DetailRow label="Bausteine"
                             value={`${selected.enabled_count || 0} / ${selected.block_count || 0} aktiv`} />
                  <DetailRow label="Schriftart" value={selected.font_family || "Helvetica"} />
                  <DetailRow label="Schriftgröße" value={`${selected.font_size_pt || 11} pt`} />
                  <DetailRow label="Geändert"
                             value={fmtDate(selected.updated_at || selected.created_at)} />
                  {selected.description && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
                        Notiz
                      </div>
                      <div className="text-[11px] text-slate-600 italic">{selected.description}</div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="lg:col-span-2 flex items-center justify-center text-slate-400 text-xs">
                <div className="text-center">
                  <Briefcase size={24} className="mx-auto text-slate-300 mb-1" />
                  Wähle eine Vertragsvorlage, um die Bausteine zu sehen.
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function FilterRow({ active, onClick, children, count, testid }) {
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={testid}
        className={`w-full flex items-center justify-between px-2 py-1 rounded-sm text-xs transition-colors ${
          active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        <span className="truncate">{children}</span>
        <span className={`text-[9px] tabular-nums ${active ? "text-white/70" : "text-slate-400"}`}>
          {count}
        </span>
      </button>
    </li>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[10px] text-slate-500">{label}</span>
      <span className="text-[11px] text-slate-800 text-right truncate">{value}</span>
    </div>
  );
}
